/**
 * Tile dispatcher: switches on `tile.id` to render the matching component.
 *
 * Unknown tile ids are silently skipped — that way a backend tile builder
 * shipped before this frontend is harmless rather than throwing.
 *
 * Spec: docs/superpowers/specs/2026-05-28-admin-analytics-tabs-design.md §6.
 */
import { NsmTile } from './NsmTile';
import { SparklineTile } from './SparklineTile';
import { QueueTile } from './QueueTile';
import { SystemPulseTile } from './SystemPulseTile';
import { RecentActionsTile } from './RecentActionsTile';
import { AppDownloadsTile } from './AppDownloadsTile';
import { PinnedAnswersTile } from '@/pages/agent/components/PinnedAnswersTile';

export interface Tile {
  id: string;
  requires: string;
  data: any;
}

export function TileRenderer({ tile }: { tile: Tile }) {
  switch (tile.id) {
    case 'nsm_weekly':
      return <NsmTile data={tile.data} />;
    case 'sparklines_7d':
      return <SparklineTile data={tile.data} />;
    case 'queue_reports':
      return (
        <QueueTile
          title="Pending reports"
          count={tile.data.count}
          link="/reports?status=pending"
        />
      );
    case 'queue_feedback':
      return <QueueTile title="Open feedback" count={tile.data.count} link="/feedback" />;
    case 'queue_photos':
      return <QueueTile title="Photos awaiting" count={tile.data.count} link="/moderation" />;
    case 'queue_flagged':
      return <QueueTile title="Flagged matches" count={tile.data.count} link="/matches?flagged=true" />;
    case 'app_downloads':
      return <AppDownloadsTile data={tile.data} />;
    case 'system_pulse':
      return <SystemPulseTile data={tile.data} />;
    case 'recent_admin_actions':
      return <RecentActionsTile data={tile.data} />;
    // ── The pinned board (spec 16.4 primitive 2). Until this arm existed the
    //    backend computed `pinned_answers` on every dashboard load and the
    //    `default:` branch below DROPPED IT SILENTLY — no error, no wrong
    //    number, nothing in the estate able to notice. The id is
    //    `PINNED_TILE_ID` in `backend/src/routes/admin-agent-library.ts`; it is
    //    written here as a LITERAL because the admin panel does not bundle the
    //    backend package, and clause 12b of
    //    `tests/unit/lint/agent-execution-boundary.test.ts` compares the two,
    //    so a rename on either side fails the build.
    case 'pinned_answers':
      return <PinnedAnswersTile data={tile.data} />;
    default:
      return null;
  }
}
